import { request, requestAPI } from '../../../../utils/request';
import {
  claimsRequest,
  claimsSuccess,
  claimsFailure,
  setDisplayClaims,
  updateClaimRequest,
  updateClaimSuccess,
  updateClaimFailure,
  assignableClaimTripsRequest,
  assignableClaimTripsFailure,
  assignableClaimTripsSuccess,
  ClaimsByCarRequest,
  ClaimsByCarSuccess,
  ClaimsByCarFailure,
  setTotalDamages,
  claimRequest,
  claimSuccess,
  claimFailure,
  claimUserReceiptsRequest,
  claimUserReceiptsSuccess,
  claimUserReceiptsFailure,
  setClaimEvents,
} from './actions';
import message from '../../../../utils/message';
import { setCarDamages, updateCarDamage } from '../cars';
import { isAfter } from 'date-fns';
import { DamageImageData, Dispatch, GetState } from '../../../../@types';
import { localize } from '../../../helpers/localize';
import { EditingClaimSource } from '../../ui/common/initialState';
import { ClaimsState } from './initialState';
import { APIFilterParams } from '../../ui/filters';
import { fetchTripInvoiceData } from '../trips';

const withTripIgnition = (claim: any) => ({
  ...claim,
  tripIgnition: isAfter(
    new Date(claim?.addedAt),
    new Date(claim?.initialIgnitionOn)
  )
    ? 'after'
    : 'before',
});

export const fetchClaims =
  (
    offset: number,
    limit: number,
    successCallback: () => void = () => {},
    filterParams?: APIFilterParams
  ) =>
  (dispatch: Dispatch, getState: GetState) => {
    requestAPI('/fleets/damages', {
      limit: limit.toString(),
      offset: offset.toString(),
      ...filterParams,
    })
      .then((pagedClaims) => {
        const updatedClaims = pagedClaims?.damages
          ? pagedClaims.damages.map(withTripIgnition)
          : [];
        dispatch(claimsSuccess(updatedClaims));
        dispatch(setDisplayClaims(updatedClaims));
        dispatch(setTotalDamages(pagedClaims?.totalCount));
        if (successCallback && typeof successCallback === 'function') {
          successCallback();
        }
      })
      .catch((err) => {
        dispatch(claimsFailure(err));
        message.error(err.message);
      });
  };

export const fetchClaimsByCarId = (carId?: string) => (dispatch: Dispatch) => {
  dispatch(ClaimsByCarRequest());
  requestAPI(`/fleets/damages/cars/${carId}`)
    .then((damages) => {
      dispatch(setCarDamages(damages));
      dispatch(ClaimsByCarSuccess());
    })
    .catch((err) => {
      dispatch(ClaimsByCarFailure());
      message.error(err.message);
    });
};

const updateClaim =
  (claimId: string, claimData: any, successCallback: (arg: any) => void) =>
  (dispatch: Dispatch) => {
    dispatch(updateClaimRequest());
    requestAPI(
      `/fleets/damages/${claimId}`,
      {},
      {
        method: 'PUT',
        body: JSON.stringify(claimData),
      }
    )
      .then((data) => {
        successCallback(data);
        dispatch(updateClaimSuccess(withTripIgnition(data)));
        if (data?.id) {
          dispatch(updateCarDamage(data));
        }
        message.success('Updated');
      })
      .catch((err) => {
        dispatch(updateClaimFailure(err));
        message.error(err.message);
      });
  };

const shouldUpdateClaim = (state: ClaimsState) => !state.updatingStatus;

export const updateClaimIfNeeded = (
  claimId: string,
  claimData: any,
  successCallback: (data: any) => void = () => {}
) => {
  return (dispatch: Dispatch, getState: GetState) => {
    if (shouldUpdateClaim(getState().userData.claims)) {
      return dispatch(updateClaim(claimId, claimData, successCallback));
    } else {
      return Promise.resolve();
    }
  };
};

export const fetchAssignableClaimTrips =
  (carId: string, beforeTripId: string) => (dispatch: Dispatch) => {
    dispatch(assignableClaimTripsRequest());
    const beforeIdObj = beforeTripId
      ? { beforeId: beforeTripId.toString() }
      : {};
    requestAPI(`/fleets/cars/${carId}/trips`, beforeIdObj)
      .then((trips) => {
        dispatch(assignableClaimTripsSuccess(trips));
      })
      .catch((err) => {
        dispatch(assignableClaimTripsFailure(err));
        message.error(err.message);
      });
  };

export const fetchClaimUserReceipts =
  (userReferralCode: string) => (dispatch: Dispatch) => {
    dispatch(claimUserReceiptsRequest());
    requestAPI('/fleets/receipts', {
      limit: '1000',
      offset: '0',
      userReferralCode,
    })
      .then((data) => {
        dispatch(claimUserReceiptsSuccess(data?.receipts || []));
      })
      .catch((err) => {
        dispatch(claimUserReceiptsFailure(err));
        message.error(err.message);
      });
  };

export const fetchClaimById =
  (claimId: string | null) => (dispatch: Dispatch) => {
    dispatch(claimRequest());
    requestAPI(`/fleets/damages/${claimId}`)
      .then((claim) => {
        if (claim.ok === undefined) {
          const updatedClaim = withTripIgnition(claim);
          dispatch(claimSuccess(updatedClaim));
          if (claim?.carId) {
            dispatch(fetchAssignableClaimTrips(claim?.carId, claim.tripId));
          }
          if (claim?.assignedTripId) {
            dispatch(
              fetchTripInvoiceData(claim.assignedTripId, (invoiceData: any) => {
                const referralCode =
                  invoiceData?.userRef || updatedClaim?.userReferralCode;
                if (referralCode) {
                  dispatch(fetchClaimUserReceipts(referralCode));
                }
              })
            );
          } else if (updatedClaim?.userReferralCode) {
            dispatch(fetchClaimUserReceipts(updatedClaim.userReferralCode));
          }
        }
      })
      .catch((err) => {
        message.error(err.message);
        dispatch(claimFailure(err));
      });
  };

export const fetchDamageEvents = (damageId: string) => (dispatch: Dispatch) => {
  if (!damageId) return;
  requestAPI(`/fleets/damages/${damageId}/events`)
    .then((events: any[]) => {
      const list = Array.isArray(events) ? events : events?.events || [];
      dispatch(setClaimEvents({ damageId, events: list }));
      dispatch(updateCarDamage({ id: damageId, events: list } as any));
    })
    .catch((err) => {
      message.error(err.message);
    });
};

export const fetchDamageEventsForCarvalooDamages =
  (damages: any[]) => (dispatch: Dispatch) => {
    if (!Array.isArray(damages)) return;
    damages.forEach((d) => {
      // Dedup on "events key present" — without this the
      // `updateCarDamage({events})` dispatch below would mutate the
      // `carDamages` array reference and re-trigger the consuming
      // useEffect, looping the API call forever.
      if (d?.reportedByCarvaloo && d?.id && !Array.isArray(d?.events)) {
        // @ts-ignore — thunk dispatch
        dispatch(fetchDamageEvents(d.id));
      }
    });
  };

export const addClaimNote =
  (claimId: string, note: string) => (dispatch: Dispatch) => {
    dispatch(updateClaimRequest());
    return requestAPI(
      `/fleets/damages/${claimId}/notes`,
      { note },
      { method: 'POST' }
    )
      .then((data) => {
        dispatch(updateClaimSuccess(withTripIgnition(data)));
        message.success('Note added');
      })
      .catch((err) => {
        dispatch(updateClaimFailure(err));
        message.error(err.message);
      });
  };

const dataUrlToUpload = (uri: string) => {
  const contentType =
    uri.match(/^data:([^;]+);base64,/)?.[1] || 'application/octet-stream';
  const binaryData = atob(uri.split(',')[1]);
  const bytes = new Uint8Array(binaryData.length);
  for (let i = 0; i < binaryData.length; i++) {
    bytes[i] = binaryData.charCodeAt(i);
  }
  return { contentType, body: bytes.buffer as any };
};

const refreshClaimImages =
  (claimId: string, source: EditingClaimSource) =>
  (dispatch: Dispatch, getState: GetState) => {
    if (source === 'car-drawer') {
      const carId =
        getState().userData.cars.carDamages?.find((d: any) => d.id === claimId)
          ?.carId ||
        getState().userData.claims.entities?.find((c: any) => c.id === claimId)
          ?.carId;
      if (carId) dispatch(fetchClaimsByCarId(carId));
    } else {
      dispatch(fetchClaimById(claimId));
    }
  };

export const uploadClaimDocument =
  (claimId: string, uri: string, comment: string) => (dispatch: Dispatch) => {
    const { contentType, body } = dataUrlToUpload(uri);
    const params: Record<string, string> = { documentType: 'OTHER' };
    if (comment) params.comment = comment;
    dispatch(updateClaimRequest());
    return requestAPI(`/fleets/damages/${claimId}/uploadDocument`, params, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body,
    })
      .then(() => {
        message.success('Document uploaded');
        dispatch(fetchClaimById(claimId));
      })
      .catch((err) => {
        dispatch(updateClaimFailure(err));
        message.error(err.message);
      });
  };

export const uploadClaimImage =
  (
    claimId: string,
    uri: string,
    source: 'claim-drawer' | 'car-drawer' = 'claim-drawer'
  ) =>
  (dispatch: Dispatch) => {
    const { contentType, body } = dataUrlToUpload(uri);
    dispatch(updateClaimRequest());
    return requestAPI(
      `/fleets/damages/${claimId}/images`,
      {},
      {
        method: 'POST',
        headers: { 'Content-Type': contentType },
        body,
      }
    )
      .then(() => {
        message.success('Image uploaded');
        dispatch(refreshClaimImages(claimId, source));
      })
      .catch((err) => {
        dispatch(updateClaimFailure(err));
        message.error(err.message);
      });
  };

export const submitAnnotatedClaimImage =
  (
    claimId: string,
    uri: string,
    sourceImage: DamageImageData | undefined,
    source: EditingClaimSource = 'claim-drawer'
  ) =>
  async (dispatch: Dispatch, getState: GetState): Promise<boolean> => {
    const { contentType, body } = dataUrlToUpload(uri);
    try {
      await requestAPI(
        `/fleets/damages/${claimId}/images`,
        {},
        {
          method: 'POST',
          headers: { 'Content-Type': contentType },
          body,
        }
      );
      const hideSource = !!sourceImage?.key && !sourceImage.hiddenFromUser;
      if (hideSource) {
        const data = await requestAPI(
          `/fleets/damages/${claimId}/images/${encodeURIComponent(
            sourceImage.key
          )}`,
          {},
          {
            method: 'PUT',
            body: JSON.stringify({ hiddenFromUser: true }),
          }
        );
        if (data?.id) {
          dispatch(updateClaimSuccess(withTripIgnition(data)));
          dispatch(updateCarDamage(data));
        }
      }
      message.success(
        hideSource
          ? localize('fleetadmin.claim.image.annotate.saved', getState) ||
              'Edited photo uploaded, original hidden'
          : localize('fleetadmin.claim.image.annotate.uploaded', getState) ||
              'Edited photo uploaded'
      );
      dispatch(refreshClaimImages(claimId, source));
      return true;
    } catch (err: any) {
      dispatch(updateClaimFailure(err));
      message.error(err.message);
      return false;
    }
  };

export const updateDamageImageVisibility =
  (damageId: string, imageKey: string, hiddenFromUser: boolean) =>
  (dispatch: Dispatch) => {
    if (!damageId || !imageKey) return Promise.resolve();
    dispatch(updateClaimRequest());
    return requestAPI(
      `/fleets/damages/${damageId}/images/${encodeURIComponent(imageKey)}`,
      {},
      {
        method: 'PUT',
        body: JSON.stringify({ hiddenFromUser }),
      }
    )
      .then((data) => {
        dispatch(updateClaimSuccess(withTripIgnition(data)));
        if (data?.id) {
          dispatch(updateCarDamage(data));
        }
        message.success(
          hiddenFromUser ? 'Photo hidden from user' : 'Photo visible to user'
        );
      })
      .catch((err) => {
        dispatch(updateClaimFailure(err));
        message.error(err.message);
      });
  };
